import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";
import { track, trackPhoneClick } from "@/lib/analytics";
import LegalNotice from "@/components/LegalNotice";
import FormConsent from "@/components/FormConsent";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent!");
      setSubmitted(true);
      track("contact_form_submitted", { subject: form.subject });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-4xl">
          <div className="eyebrow">Contact</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Let's talk about <span className="brand-gradient-text">care that fits.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">Real people answer every call and message. Here's how to reach us.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <div className="soft-card !p-6" data-testid="contact-info-phone">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">Phone</div>
                  <div className="mt-3 space-y-2">
                    {SITE.phones.map((p) => (
                      <a
                        key={p.tel}
                        href={`tel:${p.tel}`}
                        onClick={trackPhoneClick(`contact-card-${p.label.toLowerCase()}`)}
                        className="group flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white hover:border-teal-300 hover:bg-teal-50/40 px-4 py-3 transition"
                        data-testid={`contact-phone-${p.label.toLowerCase()}`}
                      >
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{p.label}</div>
                          <div className="text-lg font-heading font-semibold text-slate-900 group-hover:text-brand-navy">{p.display}</div>
                        </div>
                        <Phone className="w-4 h-4 text-teal-600 group-hover:scale-110 transition" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="soft-card flex items-start gap-4 !p-6" data-testid="contact-info-email">
              <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">Email</div>
                <a href={`mailto:${SITE.email}`} className="mt-1.5 block text-lg font-heading font-semibold text-slate-900 hover:text-brand-navy">
                  {SITE.email}
                </a>
              </div>
            </div>

            <div className="soft-card flex items-start gap-4 !p-6" data-testid="contact-info-address">
              <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">Office</div>
                <div className="mt-1.5 text-[15px] font-medium text-slate-800">
                  {SITE.address.line1}<br />{SITE.address.line2}
                </div>
              </div>
            </div>

            <div className="soft-card flex items-start gap-4 !p-6" data-testid="contact-info-hours">
              <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">Hours</div>
                <div className="mt-1.5 text-[15px] font-medium text-slate-800">
                  Office: {SITE.hours.office}<br />Care line: {SITE.hours.careLine}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="soft-card text-center" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
                <h2 className="mt-6 text-2xl font-semibold">Thanks, we got it.</h2>
                <p className="mt-3 text-slate-600">We'll respond within 1 business day. If this is urgent, please call the 24/7 care line.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="soft-card space-y-5" data-testid="contact-form">
                <LegalNotice variant="hipaa" testId="ct-hipaa-notice">
                  Please do <strong>not</strong> include sensitive medical details here. Share those securely once a
                  care coordinator reaches out.
                </LegalNotice>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field">Name *</label>
                    <input required value={form.name} onChange={upd("name")} className="input-field" data-testid="ct-name" />
                  </div>
                  <div>
                    <label className="label-field">Phone</label>
                    <input type="tel" value={form.phone} onChange={upd("phone")} className="input-field" data-testid="ct-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-field">Email *</label>
                    <input required type="email" value={form.email} onChange={upd("email")} className="input-field" data-testid="ct-email" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-field">Subject *</label>
                    <input required value={form.subject} onChange={upd("subject")} className="input-field" data-testid="ct-subject" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-field">Message *</label>
                    <textarea required rows={6} value={form.message} onChange={upd("message")} className="input-field" data-testid="ct-message" />
                  </div>
                </div>
                <FormConsent type="contact" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} testId="ct-consent" />
                <button disabled={loading} className="btn-primary" data-testid="ct-submit">
                  <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x rounded-[28px] overflow-hidden shadow-xl border border-slate-100">
          <iframe
            title="Melo Hopeful Care Office — Omaha, NE"
            width="100%" height="420" loading="lazy" allowFullScreen
            src={SITE.mapsEmbedUrl}
            data-testid="contact-map"
          />
        </div>
      </section>
    </div>
  );
}
